import React, { useContext, useEffect } from "react";
import {
  Routes as RouterRoutes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import Home from "./Home";
import SignIn from "./SignIn";
import TrialSuccess from "./Onboarding/success/TrialSuccess";
import ForgotPassword from "./ForgotPassword";
import Dashboard from "./Dashboard";
import PlaylistOverview from "./PlaylistOverview";
import SeriesOverview from "./SeriesOverview";
import ChannelOverview from "./ChannelOverview";
import MyAccount from "./MyAccount";
import Success from "./Onboarding/success/Success";
import About from "./About";
import Sitemap from "./Sitemap";
import TermsConditions from "./TermsConditions";
import PrivacyPolicy from "./PrivacyPolicy";
import CookiePolicy from "./CookiePolicy";
import Help from "./Help";
import FarmFLiXApp from "./FarmFLiXApp";
import FarmFLiXBigScreen from "./FarmFLiXBigScreen";
import OnboardingReferral from "./Onboarding/Referral";
import OnboardingPublic from "./Onboarding/Public";
import OnboardingPublicBuy from "./Onboarding/BuyPublic";
import OnboardingGiftcard from "./Onboarding/Giftcard";
import GiftcardSuccess from "./Onboarding/success/GiftcardSuccess";

// Latest
import LatestMaster from "./LatestMaster";
import LatestMasterLegacy from "./LatestMasterLegacy";
import LatestPreview from "./LatestPreview";

import Video from "./Video";

// Legacy
import VideoLegacy from "./VideoLegacy";
import PreviewVideo from "./PreviewVideo";

import AgLife from "./AgLife";
// import OnThePull from "./OnThePull";
// /post/:id is a LIVE route (#641 — the #589 "blog is dead" assumption was
// wrong for posts; real traffic). /blog/ and /category/:slug were already
// redirect-home stubs and live in VANITY_REDIRECTS below.
import Post from "./Post";
import Careers from "./Careers";
import Search from "./Search";
import Contact from "./Contact";
import NotFound from "./NotFound";
import DoorsClosed from "./DoorsClosed";
import Inbox from "./Inbox";

// Series Overview
import ClydesFarm from "./ClydesFarm";
import CarsehallFarm from "./CarsehallFarm";

// Event Pass
import EventPass from "./EventPass";

// Campaign Landing Pages
import Campaign from "./Campaign";

// link.farmflix.tv fallback for visitors without the app (#883)
import LinkResolver from "./LinkResolver";
import LinkChanged from "./LinkChanged";
// link.farmflix.tv/get-app without the app: the phone's store, or /app (#1264)
import GetAppRedirect from "./GetAppRedirect";

// Free-on-YouTube landing page (#588, #590)
import YouTubeLanding from "./YouTubeLanding";

// Watch History
import WatchHistory from "./WatchHistory";
import Favourites from "./Favourites";

// TV Activation
import TvActivation from "./TvActivation";

// Member Survey
import MemberSurvey from "./MemberSurvey";
import MemberSurveyCompleted from "./MemberSurveyCompleted";

// Welcome Back
import WelcomeBack from "./WelcomeBack";

import AutoLogin from "./AutoLogin";
import { LoggerContext } from "../store/LoggerContext";
import FreeTrialLanding from "./FreeTrialLanding";
import PublicMarketingPreferences from "./PublicMarketingPreferences";
import OnThePull from "./OnThePull";
import { OnThePullLive } from "./OnThePull/OnThePullLive";

// react-router v6 dropped v5's inline regex param constraints
// (":code([a-zA-Z0-9-_]{6})"). This wrapper reproduces them for REQUIRED
// params: a value that fails its pattern falls through to NotFound, exactly
// as v5's failed match did. (Optional params behave differently — a failing
// value meant "param absent", not 404 — those are validated inside the
// component instead; see OnboardingGiftcard / PublicMarketingPreferences
// and Routes.inventory.test.js.)
const RequireParamPattern = ({ param, pattern, element }) => {
  const params = useParams();
  const value = params[param] ?? "";
  return pattern.test(value) ? element : <NotFound />;
};

// /shop and /feature hand the visitor off to an external URL. Assigning
// location.href belongs in an effect: under StrictMode's dev double-render
// a render-body assignment would fire twice (#637).
const ExternalRedirect = ({ href }) => {
  useEffect(() => {
    window.location.href = href;
  }, [href]);
  return null;
};

const FreeTrialLandingFB = () => <FreeTrialLanding platform={"fb"} />;
const FreeTrialLandingSC = () => <FreeTrialLanding platform={"sc"} />;
const FreeTrialLandingTT = () => <FreeTrialLanding platform={"tt"} />;
const FreeTrialLandingGA = () => <FreeTrialLanding platform={"ga"} />;

// Data-driven table of one-off redirects (#589 dead-weight purge). Collapses
// what used to be ~18 near-identical <Route><Redirect/></Route> blocks —
// influencer vanity URLs, retired "coming soon" stub pages, legacy blog
// remnants, and the one-off Mammy Challenge redirect — into a single list.
// Every entry here must keep resolving to the exact same destination as
// before the refactor; see Routes.test.js for the table-driven guard.
export const VANITY_REDIRECTS = [
  // Mammy Challenge (component removed — route already redirected home)
  { path: "/themammychallenge", to: "/" },
  // Retired "coming soon." stub pages
  { path: "/straight6", to: "/" },
  { path: "/stockyards", to: "/" },
  { path: "/theloft", to: "/" },
  // Legacy blog remnants (NOT /post/:id — that route is live, #641)
  { path: "/category/:slug", to: "/" },
  { path: "/blog/", to: "/" },
  // Open Period Landing Pages follow-up + influencer vanity URLs
  { path: "/free-trial-app", to: "/app" },
  { path: "/farming", to: "/app" },
  { path: "/farmerphil", to: "/app" },
  { path: "/meatteacher", to: "/app" },
  { path: "/cowfarmerdan", to: "/app" },
  { path: "/finwalsh", to: "/app" },
  { path: "/sammylambert", to: "/app" },
  { path: "/aedinquirke", to: "/app" },
  { path: "/devonfarmgirl", to: "/app" },
  { path: "/louisecrowley", to: "/app" },
  { path: "/maighread_b", to: "/app" },
  { path: "/teeg110", to: "/app" },
  { path: "/thatfarmerdude", to: "/app" },
  { path: "/wildwelshshepherdess", to: "/app" },
  { path: "/gerry6420", to: "/app" },
  { path: "/yorkshirefarmlad", to: "/app" },
  { path: "/samonthefarm", to: "/app" },
];

// v5's non-exact routes prefix-matched deeper URLs (/about/anything served
// About); v6 needs an explicit trailing splat for that. Paths below carry
// /* wherever the v5 table was non-exact, so the served URL space is
// unchanged — Routes.inventory.test.js pins it.
const Routes = () => {
  const location = useLocation();
  const { logScreenView } = useContext(LoggerContext);

  useEffect(() => {
    logScreenView(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <RouterRoutes>
      <Route path="/" element={<Home />} />
      <Route path="/videos" element={<Dashboard />} />
      <Route path="/videos/:tag/:id/*" element={<Video />} />
      <Route path="/video-details/:id/*" element={<Video />} />
      <Route path="/legacy-videos/:id/*" element={<VideoLegacy />} />
      <Route
        path="/referral/:code/*"
        element={
          <RequireParamPattern
            param="code"
            pattern={/^[a-zA-Z0-9-_]{6}$/}
            element={<OnboardingReferral />}
          />
        }
      />
      <Route
        path="/unlock/:code/*"
        element={
          <RequireParamPattern
            param="code"
            pattern={/^[a-zA-Z0-9-_]{6}$/}
            element={<OnboardingReferral />}
          />
        }
      />
      {/* :code is optional and pattern-checked INSIDE OnboardingGiftcard —
          a failing value means "no code", not 404 (v5 behaviour) */}
      <Route path="/usemycode/:code?" element={<OnboardingGiftcard />} />
      <Route
        path="/marketing-preferences/:unsubscribeToken?"
        element={<PublicMarketingPreferences />}
      />
      <Route path="/trybuy/*" element={<Navigate to="/try" replace />} />
      <Route path="/buy-now/*" element={<Navigate to="/buy" replace />} />
      <Route path="/try/*" element={<OnboardingPublic />} />
      <Route path="/sign-up/*" element={<OnboardingPublic />} />
      <Route path="/buy/*" element={<OnboardingPublicBuy />} />
      <Route path="/preview/:id/*" element={<PreviewVideo />} />
      <Route path="/latest/*" element={<LatestMaster />} />
      <Route path="/legacy/latest/*" element={<LatestMasterLegacy />} />
      <Route path="/latest-preview" element={<LatestPreview />} />
      <Route path="/channel/:id/*" element={<ChannelOverview />} />
      <Route path="/playlist/:id/*" element={<PlaylistOverview />} />
      <Route path="/myaccount/*" element={<MyAccount />} />
      <Route path="/login/auto" element={<AutoLogin />} />
      <Route path="/login/*" element={<SignIn />} />
      <Route path="/forgotpassword/*" element={<ForgotPassword />} />
      <Route path="/success" element={<Success />} />
      <Route path="/success/trial" element={<TrialSuccess />} />
      <Route path="/success/giftcard" element={<GiftcardSuccess />} />
      <Route path="/throwbackthursday/*" element={<DoorsClosed />} />
      <Route path="/about/*" element={<About />} />
      <Route path="/terms/*" element={<TermsConditions />} />
      <Route path="/sitemap/*" element={<Sitemap />} />
      <Route path="/careers/*" element={<Careers />} />
      <Route path="/search/*" element={<Search />} />
      <Route path="/privacy-policy/*" element={<PrivacyPolicy />} />
      <Route path="/cookiepolicy/*" element={<CookiePolicy />} />
      <Route path="/contact/*" element={<Contact />} />
      <Route path="/inbox/*" element={<Inbox />} />
      {/* Series Overview  */}
      <Route path="/series/:id/*" element={<SeriesOverview />} />
      {/* Watch History */}
      <Route path="/watch-history/*" element={<WatchHistory />} />
      {/* Favourites  */}
      <Route path="/favourites/*" element={<Favourites />} />
      {/* TV Activation - log in a TV with the code it shows */}
      <Route path="/tv" element={<TvActivation />} />
      <Route
        path="/shop/*"
        element={<ExternalRedirect href="https://shop.farmflix.tv/" />}
      />
      <Route
        path="/feature/*"
        element={
          <ExternalRedirect href="https://docs.google.com/forms/u/6/d/1b5kU5knpz0Qznt7yemrIatNCEUK3Q6G7_UlbS1WazTs/edit?pli=1" />
        }
      />
      <Route path="/help/*" element={<Help />} />
      <Route path="/app" element={<FarmFLiXApp />} />
      <Route path="/get-app" element={<GetAppRedirect />} />
      <Route path="/bigscreen" element={<FarmFLiXBigScreen />} />
      <Route path="/farmflix-app/*" element={<FarmFLiXApp />} />
      <Route path="/aglife/*" element={<AgLife />} />
      <Route path="/onthepull" element={<OnThePull />} />
      <Route
        path="/onthepull/:view/*"
        element={
          <RequireParamPattern
            param="view"
            pattern={/^(leaderboard|matches)$/}
            element={<OnThePullLive />}
          />
        }
      />

      <Route path="/post/:id/*" element={<Post />} />
      {/* Open Period Landing Pages */}
      <Route path="/ga/free-trial" element={<FreeTrialLandingGA />} />
      <Route path="/sc/free-trial" element={<FreeTrialLandingSC />} />
      <Route path="/tt/free-trial" element={<FreeTrialLandingTT />} />
      <Route path="/fb/free-trial" element={<FreeTrialLandingFB />} />
      <Route path="/free-trial" element={<FreeTrialLandingFB />} />
      {/* Vanity redirects: influencer URLs, retired stub/blog pages, and
          the one-off Mammy Challenge redirect — see VANITY_REDIRECTS above */}
      {VANITY_REDIRECTS.map(({ path: redirectPath, to }) => (
        // The trailing-slash strip keeps "/blog/" from producing an
        // invalid "//*" path; "/blog/*" matches /blog and /blog/ alike.
        <Route
          key={redirectPath}
          path={`${redirectPath.replace(/\/$/, "")}/*`}
          element={<Navigate to={to} replace />}
        />
      ))}
      <Route path="/clydesfarm/*" element={<ClydesFarm />} />
      <Route path="/carsehallfarm/*" element={<CarsehallFarm />} />
      <Route path="/member-survey/*" element={<MemberSurvey />} />
      <Route
        path="/member-survey-completed/*"
        element={<MemberSurveyCompleted />}
      />
      <Route path="/eventpass/*" element={<EventPass />} />
      {/* Campaign landing pages - server-driven from the overview payload */}
      <Route path="/c/:slug/*" element={<Campaign />} />
      {/* link.farmflix.tv/<path> lands here when the app isn't installed;
          resolves to a page, a campaign, or /app (see LinkResolver) */}
      <Route path="/link/*" element={<LinkResolver />} />
      {/* An episode link whose episode can't be found (#888) */}
      <Route path="/link-changed" element={<LinkChanged />} />
      {/* A free YouTube video in the official embed; no id goes Home */}
      <Route path="/youtube/:id?" element={<YouTubeLanding />} />

      <Route path="/welcomeback" element={<WelcomeBack />} />
      <Route path="/undefined" element={<Navigate to="/latest" replace />} />
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
  );
};

export default Routes;
